import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    // Skip loading the video for users who prefer reduced motion; the
    // stylesheet already hides it, so downloading it would be wasted bandwidth.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (!firstEntry || !firstEntry.isIntersecting) return;
        intersectionObserver.disconnect();

        const { videoSources, videoElement } = this.refs;

        for (const source of videoSources) {
          const { videoSource } = source.dataset;

          if (videoSource) source.setAttribute('src', videoSource);
        }

        videoElement.load();
      },
      { rootMargin: '200px' }
    );

    intersectionObserver.observe(this);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
